/**
 * Lifecycle event schemas — signup-and-billing Phase 1.
 *
 * Persisted in the `lifecycle_events` table (created by migration
 * `20260504120000_signup_billing_phase_1.sql`). DISTINCT from field-level
 * `provenance_events` — that table's `source_tag` CHECK enum
 * (`USER | SYSTEM_DEFAULT | MARKET_DATA | INFERRED | DERIVED | SCENARIO`) is
 * a poor fit for invite/approval/billing categories.
 *
 * 26 event types span 6 categories. signup-and-billing Phase 1 shipped full
 * payload schemas for the 4 invite/approval events; external-sharing Phase 1
 * added 9 deal-link / fork events; signup-billing-mvp Phase 2 replaces the
 * placeholder schemas for the MVP-fired billing events with real ones + adds
 * WELCOME_TOUR_SHOWN / SUBSCRIPTION_TIER_CONFIG_CHANGED / PRICING_CONFIG_CHANGED.
 * conversation-model-versioning Phase 1 Session 5 added MODEL_LIFECYCLE category
 * + 3 event types (MODEL_VERSION_CREATED, MODEL_VERSION_RESTORED,
 * UNDERWRITING_VERSION_ACCEPTED).
 *
 * Validated at write time by `storeLifecycleEvent` in
 * `packages/supabase/src/queries/lifecycle-events.ts` — malformed payloads
 * fail loudly during dev rather than landing as garbage jsonb.
 */
import { z } from 'zod';
import { viewerKindSchema } from './sharing.js';
import { runEligibilityReasonEnum, billingOverrideKindEnum, } from './billing.types.js';
// ── Category enum (matches lifecycle_events.category CHECK) ────────────────────
// Migration `20260602000000_shared_links_and_versioning.sql` extended the CHECK
// to include DEAL_LINK + SHARE_FORK (external-sharing Phase 1, 2026-05-08).
export const lifecycleEventCategoryEnum = z.enum([
    'USER_LIFECYCLE',
    'APPROVAL',
    'BILLING_TRANSACTION',
    'DEAL_LINK',
    'SHARE_FORK',
    'MODEL_LIFECYCLE',
]);
// ── Event type frozen union ────────────────────────────────────────────────────
// 26 strings. Adding a new event type requires extending this constant AND
// adding a payload schema in LIFECYCLE_PAYLOAD_SCHEMAS below — runtime
// validator throws UNKNOWN_EVENT_TYPE on cache miss.
export const LIFECYCLE_EVENT_TYPES = {
    // USER_LIFECYCLE (2)
    INVITE_SENT: 'INVITE_SENT',
    // signup-billing-mvp Phase 2 (schema) / Phase 3 (UI fires it)
    WELCOME_TOUR_SHOWN: 'WELCOME_TOUR_SHOWN',
    // APPROVAL (3)
    APPROVAL_REQUESTED: 'APPROVAL_REQUESTED',
    APPROVAL_GRANTED: 'APPROVAL_GRANTED',
    APPROVAL_DENIED: 'APPROVAL_DENIED',
    // BILLING_TRANSACTION (10) — signup-billing-mvp Phase 2 ships real payload
    // schemas for the MVP-fired events; PAYG_PURCHASE_SUCCEEDED + AUTO_RELOAD_FIRED
    // stay defined-but-unfired (v1.1); SUBSCRIPTION_TIER_CONFIG_CHANGED +
    // PRICING_CONFIG_CHANGED added Phase 2 (schema) / Phase 5 (admin UI fires them).
    // in-app-checkout Phase 1 added BESPOKE_REQUESTED.
    CREDIT_BALANCE_INITIALIZED: 'CREDIT_BALANCE_INITIALIZED',
    PAYG_PURCHASE_SUCCEEDED: 'PAYG_PURCHASE_SUCCEEDED',
    SUBSCRIPTION_TIER_CHANGED: 'SUBSCRIPTION_TIER_CHANGED',
    AUTO_RELOAD_FIRED: 'AUTO_RELOAD_FIRED',
    RUN_BLOCKED_BALANCE_EXHAUSTED: 'RUN_BLOCKED_BALANCE_EXHAUSTED',
    RUN_TERMINATED_AT_HARD_CEILING: 'RUN_TERMINATED_AT_HARD_CEILING',
    ADMIN_BILLING_OVERRIDE_APPLIED: 'ADMIN_BILLING_OVERRIDE_APPLIED',
    SUBSCRIPTION_TIER_CONFIG_CHANGED: 'SUBSCRIPTION_TIER_CONFIG_CHANGED',
    PRICING_CONFIG_CHANGED: 'PRICING_CONFIG_CHANGED',
    // in-app-checkout Phase 1 (2026-06-05) — user clicked "Contact sales" on a
    // contact_only tier; recorded server-side + sets user_profiles.bespoke_requested_at.
    BESPOKE_REQUESTED: 'BESPOKE_REQUESTED',
    // DEAL_LINK (7) — external-sharing Phase 1, 2026-05-08
    DEAL_LINK_CREATED: 'DEAL_LINK_CREATED',
    DEAL_LINK_VIEWED: 'DEAL_LINK_VIEWED',
    DEAL_LINK_QUESTION_ASKED: 'DEAL_LINK_QUESTION_ASKED',
    DEAL_LINK_GATE_HIT: 'DEAL_LINK_GATE_HIT',
    DEAL_LINK_RECIPIENT_SIGNED_UP: 'DEAL_LINK_RECIPIENT_SIGNED_UP',
    DEAL_LINK_REVOKED: 'DEAL_LINK_REVOKED',
    DEAL_LINK_RECIPIENT_REQUESTED_ACCESS: 'DEAL_LINK_RECIPIENT_REQUESTED_ACCESS',
    // SHARE_FORK (2) — external-sharing Phase 1, 2026-05-08
    SHARE_FORK_CREATED: 'SHARE_FORK_CREATED',
    SHARE_FORK_RETURNED: 'SHARE_FORK_RETURNED',
    // MODEL_LIFECYCLE (3) — conversation-model-versioning Phase 1 Session 5, 2026-05-16
    MODEL_VERSION_CREATED: 'MODEL_VERSION_CREATED',
    MODEL_VERSION_RESTORED: 'MODEL_VERSION_RESTORED',
    UNDERWRITING_VERSION_ACCEPTED: 'UNDERWRITING_VERSION_ACCEPTED',
};
// ── Per-event payload schemas (Phase 1) ────────────────────────────────────────
export const invitePayloadSchema = z.object({
    invited_email: z.string().email(),
    invited_by_user_id: z.string().uuid(),
    pre_filled_first_name: z.string().nullable(),
    pre_filled_last_name: z.string().nullable(),
});
export const approvalRequestedPayloadSchema = z.object({
    /** Wizard step 1 — firm name only. First/last name lives in
     * `auth.users.raw_user_meta_data`; admin joins for names at approval-review
     * time. Renamed from `name_firm_raw` per /spec-review gap-analysis. */
    firm_raw: z.string(),
    role_raw: z.string(),
    asset_classes_raw: z.string(),
    markets_raw: z.string(),
    focus_raw: z.string(),
    volume_raw: z.string(),
    entered_via: z.enum(['self_signup', 'invite']),
});
export const approvalGrantedPayloadSchema = z.object({
    approver_user_id: z.string().uuid(),
    founding_partner_flag: z.boolean(),
    /**
     * Custom monthly grant if Founding Partner. NULL means default tier grant
     * (= subscription_tiers.founding_partner.usage_multiplier × base_tier_price).
     * Unit is **COGS-credits**, NOT dollars. At base_ratio_subscription_to_cogs=20.0
     * the founding-partner default 1996 COGS ≈ $99.80 of Anthropic spend.
     * Spec §2 + §11 (no `_usd` on COGS-credit fields).
     */
    founding_partner_grant_cogs: z.number().nullable(),
    /** Structured fields admin populated at approval (optional — v1 leaves these
     * null per spec; admin populates manually via /admin?tab=users). */
    structured_role: z.string().nullable(),
    structured_focus: z.enum(['operating', 'development', 'both']).nullable(),
});
export const approvalDeniedPayloadSchema = z.object({
    approver_user_id: z.string().uuid(),
    reason_text: z.string(),
});
// ── Placeholder schema — only the v1.1-reserved billing events ────────────────
// signup-billing-mvp Phase 2 replaced the placeholder map entries for every
// MVP-fired billing event with real schemas (below). PAYG_PURCHASE_SUCCEEDED +
// AUTO_RELOAD_FIRED stay defined-but-unfired (no PAYG in the MVP — research/
// feature.md § 8); they keep the permissive placeholder until the v1.1
// pay-as-you-go feature lands proper shapes.
export const billingPlaceholderPayloadSchema = z.record(z.string(), z.unknown());
// ── Billing payload schemas (signup-billing-mvp Phase 2) ──────────────────────
// COGS-credit unit semantics: `monthly_grant_cogs` / `consumed_cogs_at_termination`
// are COGS-credit counts (where `pricing_config.base_ratio_subscription_to_cogs`
// credits == $1 of Anthropic spend), NOT dollars.
export const creditBalanceInitializedPayloadSchema = z.object({
    user_id: z.string().uuid(),
    tier_id: z.string(),
    monthly_grant_cogs: z.number(),
});
export const subscriptionTierChangedPayloadSchema = z.object({
    user_id: z.string().uuid(),
    prior_tier_id: z.string().nullable(),
    new_tier_id: z.string(),
    monthly_grant_cogs: z.number(),
    admin_override: z.boolean(),
});
export const runBlockedBalanceExhaustedPayloadSchema = z.object({
    reason: runEligibilityReasonEnum,
    conversation_id: z.string().uuid().optional(),
});
export const runTerminatedAtHardCeilingPayloadSchema = z.object({
    run_id: z.string(),
    /** COGS credits consumed when the per-run hard ceiling tripped. */
    consumed_cogs_at_termination: z.number(),
    terminating_kind: z.enum(['llm', 'tool']),
    terminating_model_or_tool: z.string(),
});
export const adminBillingOverrideAppliedPayloadSchema = z.object({
    admin_user_id: z.string().uuid(),
    target_user_id: z.string().uuid(),
    kind: billingOverrideKindEnum,
    prior_value_jsonb: z.unknown().nullable(),
    new_value_jsonb: z.unknown().nullable(),
    reason_text: z.string().nullable(),
});
export const subscriptionTierConfigChangedPayloadSchema = z.object({
    admin_user_id: z.string().uuid(),
    tier_id: z.string(),
    action: z.enum(['created', 'updated', 'deactivated']),
    changed_fields: z.array(z.string()),
    prior_version: z.number().int().nullable(),
    new_version: z.number().int(),
    prior_value_jsonb: z.unknown().nullable(),
    new_value_jsonb: z.unknown().nullable(),
    /** Re-derived COGS-credit grant for this tier — = `base_tier_price × usage_multiplier`. */
    derived_monthly_grant_cogs: z.number(),
});
export const pricingConfigChangedPayloadSchema = z.object({
    admin_user_id: z.string().uuid(),
    prior_value_jsonb: z.unknown().nullable(),
    new_value_jsonb: z.unknown(),
    prior_version: z.number().int(),
    new_version: z.number().int(),
    changed_fields: z.array(z.string()),
});
export const welcomeTourShownPayloadSchema = z.object({
    user_id: z.string().uuid(),
    first_shown_at: z.string(),
    version: z.literal('v1'),
});
// ── DEAL_LINK payloads (external-sharing Phase 1) ──────────────────────────────
export const dealLinkCreatedPayloadSchema = z.object({
    shareId: z.string().uuid(),
    conversationId: z.string().uuid(),
    ownerUserId: z.string().uuid(),
    recipientEmail: z.string().email(),
    recipientUserId: z.string().uuid().optional(),
    recipientKind: z.enum(['internal', 'external']),
    parentShareId: z.string().uuid().optional(),
});
export const dealLinkViewedPayloadSchema = z.object({
    shareId: z.string().uuid(),
    viewerSessionId: z.string().optional(),
    recipientUserId: z.string().uuid().optional(),
    viewerKind: viewerKindSchema,
    userAgent: z.string().optional(),
});
export const dealLinkQuestionAskedPayloadSchema = z.object({
    shareId: z.string().uuid(),
    viewerSessionId: z.string(),
    count: z.number().int().min(1).max(3),
    gateActive: z.boolean(),
});
export const dealLinkGateHitPayloadSchema = z.object({
    shareId: z.string().uuid(),
    viewerSessionId: z.string(),
    count: z.literal(3),
});
export const dealLinkRecipientSignedUpPayloadSchema = z.object({
    shareId: z.string().uuid(),
    recipientUserId: z.string().uuid(),
    recipientEmail: z.string().email(),
    ownerUserId: z.string().uuid(),
});
export const dealLinkRevokedPayloadSchema = z.object({
    shareId: z.string().uuid(),
    ownerUserId: z.string().uuid(),
    revokedBy: z.string().uuid(),
    recipientUserId: z.string().uuid().optional(),
});
export const dealLinkRecipientRequestedAccessPayloadSchema = z.object({
    shareId: z.string().uuid(),
    recipientUserId: z.string().uuid(),
    ownerUserId: z.string().uuid(),
});
// ── SHARE_FORK payloads (external-sharing Phase 1) ─────────────────────────────
export const shareForkCreatedPayloadSchema = z.object({
    shareId: z.string().uuid(),
    parentConversationId: z.string().uuid(),
    forkConversationId: z.string().uuid(),
    forkOwnerUserId: z.string().uuid(),
});
export const shareForkReturnedPayloadSchema = z.object({
    originalShareId: z.string().uuid(),
    parentShareId: z.string().uuid(),
    returnShareId: z.string().uuid(),
    forkOwnerUserId: z.string().uuid(),
    originalOwnerUserId: z.string().uuid(),
});
// ── MODEL_LIFECYCLE payloads (conversation-model-versioning Phase 1 Session 5) ──
export const modelVersionCreatedPayloadSchema = z.object({
    conversationId: z.string().uuid(),
    versionId: z.string().uuid(),
    seq: z.number().int(),
    label: z.string(),
    modelId: z.string(),
    versionKind: z.enum(['manual', 'safety']), // CREATED fires for 'manual' only (P2 logic); schema permits both for type-safety
});
export const modelVersionRestoredPayloadSchema = z.object({
    conversationId: z.string().uuid(),
    restoredVersionId: z.string().uuid(),
    // safetyVersionId REMOVED — ADR 0028 (2026-05-18): restore is now destructive;
    // no safety snapshot is auto-created. Legacy rows with safetyVersionId still read
    // (no migration); we stop producing them. `versionKind='safety'` enum kept intact.
    previousCurrentVersionId: z.string().uuid().nullable(),
});
export const underwritingVersionAcceptedPayloadSchema = z.object({
    conversationId: z.string().uuid(),
    versionId: z.string().uuid(),
    previousVersionId: z.string().uuid().nullable(), // null on first-accept; set on every re-point (spec §5)
});
// ── BILLING_TRANSACTION payloads (in-app-checkout Phase 1) ────────────────────
export const bespokeRequestedPayloadSchema = z.object({
    user_id: z.string().uuid(),
    /** The contact_only tier the user is requesting access to. */
    firm_name: z.string().nullable(),
    /** User's email address at time of request. */
    email: z.string(),
    /** ISO-8601 timestamp of the request. */
    requested_at: z.string(),
});
// ── Lifecycle event row (matches lifecycle_events table) ───────────────────────
export const lifecycleEventSchema = z.object({
    id: z.string().uuid(),
    category: lifecycleEventCategoryEnum,
    /** Free-form text at the DB level (no CHECK on event_type — application-side
     * validation via LIFECYCLE_PAYLOAD_SCHEMAS map is the lock). */
    event_type: z.string(),
    user_id: z.string().uuid().nullable(),
    actor_user_id: z.string().uuid().nullable(),
    payload: z.record(z.string(), z.unknown()),
    created_at: z.string(),
});
// ── Event-type → payload-schema map ────────────────────────────────────────────
// Used by `storeLifecycleEvent` (in supabase queries) to validate payloads at
// write time. Adding a new event_type to LIFECYCLE_EVENT_TYPES MUST add a row
// here; otherwise storeLifecycleEvent returns UNKNOWN_EVENT_TYPE.
export const LIFECYCLE_PAYLOAD_SCHEMAS = {
    INVITE_SENT: invitePayloadSchema,
    WELCOME_TOUR_SHOWN: welcomeTourShownPayloadSchema,
    APPROVAL_REQUESTED: approvalRequestedPayloadSchema,
    APPROVAL_GRANTED: approvalGrantedPayloadSchema,
    APPROVAL_DENIED: approvalDeniedPayloadSchema,
    // signup-billing-mvp Phase 2 — real schemas for the MVP-fired billing events.
    CREDIT_BALANCE_INITIALIZED: creditBalanceInitializedPayloadSchema,
    // v1.1-reserved (no PAYG in MVP) — keep permissive placeholder.
    PAYG_PURCHASE_SUCCEEDED: billingPlaceholderPayloadSchema,
    SUBSCRIPTION_TIER_CHANGED: subscriptionTierChangedPayloadSchema,
    // v1.1-reserved — keep permissive placeholder.
    AUTO_RELOAD_FIRED: billingPlaceholderPayloadSchema,
    RUN_BLOCKED_BALANCE_EXHAUSTED: runBlockedBalanceExhaustedPayloadSchema,
    RUN_TERMINATED_AT_HARD_CEILING: runTerminatedAtHardCeilingPayloadSchema,
    ADMIN_BILLING_OVERRIDE_APPLIED: adminBillingOverrideAppliedPayloadSchema,
    SUBSCRIPTION_TIER_CONFIG_CHANGED: subscriptionTierConfigChangedPayloadSchema,
    PRICING_CONFIG_CHANGED: pricingConfigChangedPayloadSchema,
    // in-app-checkout Phase 1 (2026-06-05)
    BESPOKE_REQUESTED: bespokeRequestedPayloadSchema,
    // external-sharing Phase 1 (2026-05-08)
    DEAL_LINK_CREATED: dealLinkCreatedPayloadSchema,
    DEAL_LINK_VIEWED: dealLinkViewedPayloadSchema,
    DEAL_LINK_QUESTION_ASKED: dealLinkQuestionAskedPayloadSchema,
    DEAL_LINK_GATE_HIT: dealLinkGateHitPayloadSchema,
    DEAL_LINK_RECIPIENT_SIGNED_UP: dealLinkRecipientSignedUpPayloadSchema,
    DEAL_LINK_REVOKED: dealLinkRevokedPayloadSchema,
    DEAL_LINK_RECIPIENT_REQUESTED_ACCESS: dealLinkRecipientRequestedAccessPayloadSchema,
    SHARE_FORK_CREATED: shareForkCreatedPayloadSchema,
    SHARE_FORK_RETURNED: shareForkReturnedPayloadSchema,
    // conversation-model-versioning Phase 1 Session 5, 2026-05-16
    MODEL_VERSION_CREATED: modelVersionCreatedPayloadSchema,
    MODEL_VERSION_RESTORED: modelVersionRestoredPayloadSchema,
    UNDERWRITING_VERSION_ACCEPTED: underwritingVersionAcceptedPayloadSchema,
};
